import React from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import DataListBodyRow from "../../../../../../../../components/data-list/data-list-body/data-list-body-row";
import Avatar from "../../../../../../../../components/avatar";
import {API_ROOT} from "../../../../../../../../utils";
import Select from "../../../../../../../../components/select";
import UserStatuses
    from "../../../../../../../sharing/components/sharing-details-popup/components/add-collaborators-popup/components/list-of-collaborators/components/list-of-collaborators-details/components/list-of-collaborators-details-content/components/collaborator-item/components/user-statuses";
import Sprite from "../../../../../../../../components/sprite/components/status";
import Button from "../../../../../../../../components/button";
import Icon from "../../../../../../../../components/icon";
import Labels from "./components/labels";
import {confirmFreeLicensesPopup} from "../../../../../../../../../actions/popupActions";
import {putUpdateRole, setSelectedUserIds} from "../../../../../../../../../actions/usersListActions";

function UserList ({ collaborator, newRole, onRoleChange, isUpdatingRole, onRoleUpdate }) {

    const profile = useSelector(state => state.profileReducer.getProfile);
    const selectedUserIds = useSelector(state => state.usersListReducer.selectedUserIds);

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const isAdminRow = collaborator.role === 'ADMINISTRATOR';
    const isSelectable = !isAdminRow && collaborator.editable !== false && profile && profile.role === 'ADMINISTRATOR';
    const isSelected = selectedUserIds.includes(collaborator.id);

    const handleToggleCheckbox = (e) => {
        e.stopPropagation();
        if (!isSelectable) {
            return;
        }
        if (isSelected) {
            dispatch(setSelectedUserIds(selectedUserIds.filter(id => id !== collaborator.id)));
        } else {
            dispatch(setSelectedUserIds([...selectedUserIds, collaborator.id]));
        }
    };

    const handleFreeLicenses = (e) => {
        if (isSelected) {
            return;
        }
        dispatch(confirmFreeLicensesPopup(e.currentTarget.dataset.collaboratorId))
    };

	const handleChangeRole = (e) => {
        if (isSelected) {
            return;
        }
		onRoleChange(collaborator.id, e.currentTarget.value);
	};

    const handleClickUpdateRole = () => {
        if (isSelected) {
            return;
        }
	    onRoleUpdate(collaborator.id, true);
        dispatch(putUpdateRole({
            userId: collaborator.id,
            role: newRole
        }))
    };

    const checkboxTooltip = isAdminRow
        ? t("adminCannotBeSelected", "Administrators cannot be selected or restricted.")
        : isSelected
            ? t("tooltipUnselectUserForBulk", "Click to unselect this user from the bulk role update.")
            : t("tooltipSelectUserForBulk", "Select this user to update their role in bulk.");

    const roleSelectTooltip = isSelected
        ? t("tooltipRowDisabledDuringBulk", "Individual actions are disabled while this user is selected for a bulk role update.")
        : newRole === 'RESTRICTED_USER'
            ? t("tooltipRoleRestrictedDesc", "Restricted User: can view/edit labels shared with them, but cannot share labels or consume additional licenses.")
            : newRole === 'ADMINISTRATOR'
                ? t("tooltipRoleAdminDesc", "Admin: full control over users, licenses, and domain settings.")
                : t("tooltipRoleUserDesc", "Full User: can share labels and contacts with other users.");

    return (
        <DataListBodyRow key={collaborator.id} className={isSelected ? 'user-row-selected' : ''}>
            <div className="col-3 d-flex align-items-center">
                <div
                    className={`custom-control custom-checkbox mr-3 cf-tooltip-wrap cf-tooltip-wrap--right${!isSelectable ? ' admin-checkbox-disabled' : ''}`}
                    data-tooltip={checkboxTooltip}
                >
                    <input
                        type="checkbox"
                        className="custom-control-input"
                        id={`user-select-checkbox-${collaborator.id}`}
                        checked={isSelected}
                        disabled={!isSelectable}
                        onChange={handleToggleCheckbox}
                    />
                    <label
                        className="custom-control-label"
                        htmlFor={`user-select-checkbox-${collaborator.id}`}
                    />
                </div>
                <Avatar size={36} className="mr-3 border-0" src={`${API_ROOT}/account/avatar/${collaborator.id}`} />
                <div className="text-truncate">
                    <div><strong>{collaborator.name}</strong></div>
                    <div>{collaborator.email}</div>
                </div>
            </div>
            <div className="col-1 text-center">
                <span className="cf-tooltip-wrap" data-tooltip={roleSelectTooltip}>
                    <Select
                        disabled={collaborator.editable === false || isSelected}
                        onChange={ handleChangeRole }
                        style={{
                            fontSize: "1em",
                            opacity: isSelected ? 0.55 : 1,
                            cursor: (collaborator.editable === false || isSelected) ? 'not-allowed' : 'pointer',
                            backgroundColor: newRole === 'ADMINISTRATOR'
                                ? '#efe3e9'
                                : newRole === 'BUYER'
                                    ? '#f1e0ce'
                                    : newRole === 'RESTRICTED_USER'
                                        ? '#fff3cd'
                                        : '#dbe4eb'
                        }}
                        value={newRole}
                        name={`user-role-${collaborator.id}`}
                        id={`user-role-${collaborator.id}`}
                    >
                        <option value="ADMINISTRATOR">{t("roleAdmin", "Admin")}</option>
                        <option value="USER">{t("roleUser", "User")}</option>
                        <option value="RESTRICTED_USER">{t("roleRestrictedUser", "Restricted User")}</option>
                    </Select>
                </span>
            </div>
            <div className="col-2 text-center">
                <UserStatuses
                    collaboratorId={collaborator.id}
                    collaboratorEmail={collaborator.email}
                    collaboratorType={collaborator.type}
                    collaboratorStatuses={collaborator.statuses}
                />
            </div>
            <div className="col-1 text-center">
                <Sprite status={collaborator.statuses.licenseLimitReached || collaborator.statuses.unusedLicense ? 'NOT_LICENSED' : 'LICENSED'} width={20} height={20} />
            </div>
            <div className="col-1 text-center">
                {
                    collaborator.id !== profile.id && profile.role === "ADMINISTRATOR"
                        ?
                        <span
                            className="cf-tooltip-wrap"
                            data-tooltip={
                                isSelected
                                    ? t("tooltipRowDisabledDuringBulk", "Individual actions are disabled while this user is selected for a bulk role update.")
                                    : t("tooltipFreeUserLicense", "Unshare all labels and free up this user's license.")
                            }
                        >
                            <Button
                                data-collaborator-id={collaborator.id}
                                onClick={handleFreeLicenses}
                                disabled={isSelected}
                                className={`btn-link free-licenses${isSelected ? ' disabled' : ''}`}
                            >
                                <Icon name="user-times"/>
                            </Button>
                        </span>
                        :
                        null
                }
            </div>
            <div className="col-2">
                <Labels collaborator={collaborator} disabled={isSelected}/>
            </div>
            <div className="col-2 text-center">
                {
                    !isSelected && collaborator.role !== newRole
                    ?
                        isUpdatingRole
                        ?
                            <Button className="btn-link text-nowrap font-weight-normal" disabled={true}>
                                <Icon animated={'pulse'} name="spinner"/>
                            </Button>
                        :
	                        <Button className="btn-link text-nowrap font-weight-normal" onClick={ handleClickUpdateRole }>
		                        {t("saveChanges", "Save changes")}
	                        </Button>
                    :
                        ''
                }
            </div>
        </DataListBodyRow>
    )
}

export default UserList

